import { Component, HostListener, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { sweetAlert } from 'src/app/utils/swal';
import { SharedDataService } from 'src/app/service/shared-service/shared-data.service';
import { FleetService } from '../fleet-service/fleet.service';
import { ParserService } from '../../broker-parser/planner-service/parser-service';

@Component({
  selector: 'app-fleetio-iframe',
  templateUrl: './fleetio-iframe.component.html',
  styleUrls: ['./fleetio-iframe.component.css']
})
export class FleetioIframeComponent implements OnInit {
  portalUrl: SafeResourceUrl = '';
  isIframeLoading = true;
  private readonly fleetioOrigin = new URL(environment.FLEET_PANEL_BASE_URL).origin;
  private isLoggingIn = false;
  private lastLoginAt = 0;
  private user: any;

  constructor(
    private fleetService: FleetService,
    private parserService: ParserService,
    private sanitizer: DomSanitizer,
    private sharedDataService: SharedDataService
  ) { }

  ngOnInit() {
    this.sharedDataService.getUser().pipe(take(1)).subscribe(user => {
      if (user) {
        this.user = user;
        this.login();
      }
    });
  }

  // The panel never shows its own login page; when its session expires it
  // asks for a fresh token and the iframe is reloaded with it.
  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    const data = event.data || {};
    // 5s guard so a token the panel keeps rejecting cannot loop.
    if (event.origin === this.fleetioOrigin && data.source === 'fleetio' && data.type === 'SESSION_EXPIRED' && Date.now() - this.lastLoginAt > 5000) {
      this.login();
    }
  }

  // Same login as fleet-list: verify-callback returns the token (and the
  // signature, which is persisted on the FMS broker setting).
  private async login() {
    if (this.isLoggingIn || !this.user) {
      return;
    }
    this.isLoggingIn = true;
    this.lastLoginAt = Date.now();
    this.isIframeLoading = true;
    try {
      const nemtToken = JSON.parse(localStorage.getItem('token'));
      const res = await this.fleetService.getBrokerSettingList().toPromise();
      const fmsConfig = res.find((item: any) => item.name === 'fms');
      const loginRes = await this.fleetService.externalLogin({
        ...this.user,
        token: fmsConfig && fmsConfig.orgId ? fmsConfig.orgId : null,
        fmsConfiguration: fmsConfig ? fmsConfig : null,
        nemtToken
      }).toPromise() as any;

      if (!loginRes.token) {
        sweetAlert('Error', 'Invalid credentials', 'error', 'OK');
        return;
      }

      if (fmsConfig && loginRes.signature && fmsConfig.orgId !== loginRes.signature) {
        try {
          await this.parserService.updataBrokerSetting({
            ...fmsConfig,
            orgId: loginRes.signature
          }).toPromise();
        } catch (e) {
          console.error('Failed to save FMS signature', e);
        }
      }

      this.loadPortal(loginRes.token);
    } catch (err) {
      sweetAlert('Error', err || 'Something went wrong', 'error', 'OK');
    } finally {
      this.isLoggingIn = false;
    }
  }

  private loadPortal(token: string) {
    // `sidebar=false` makes the Fleetio panel render without its own sidebar and user menu.
    const url = `${environment.FLEET_PANEL_BASE_URL}sos?token=${encodeURIComponent(token)}&sidebar=false&redirect=${encodeURIComponent('/')}`;
    this.portalUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
